import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { FaShoppingBasket } from "react-icons/fa";
import { colorBasketNumberBadgeSx } from "../../../admin/jigsaw/play/AdminColorBasketMiniMap";
import {
    BOARD_SIZE,
    PieceVisual,
    SVG_SIZE,
    pieceVisualRenderSizePx,
} from "../board/PieceVisual";
import ScreenshotBoard from "../board/ScreenshotBoard";
import {
    buildScatteredBoardPlacements,
    homePositionForPiece,
} from "../lib/screenshotPieceLayout";
import { useScreenshotTranslation } from "../../../lib/screenshotI18n";
import { colorBasketShellSx } from "../styles/screenshotStyles";

const JM = "mobileScreenshot.jigsaw";

/** Marketing board uses a 6×6 path grid. */
const MARKETING_GRID = 6;

/** 6×6 marketing trays — 3 larger baskets (12 pieces each). */
const DEMO_BASKETS_6 = [
    {
        index: 0,
        dominantColorHex: "#5B9BD5",
        pieceIds: [0, 1, 2, 3, 6, 7, 8, 9, 12, 13, 14, 15],
    },
    {
        index: 1,
        dominantColorHex: "#E8A45A",
        pieceIds: [4, 5, 10, 11, 16, 17, 22, 23, 28, 29, 34, 35],
    },
    {
        index: 2,
        dominantColorHex: "#3BA666",
        pieceIds: [18, 19, 20, 21, 24, 25, 26, 27, 30, 31, 32, 33],
    },
];

function buildSquarePiecePaths(grid) {
    const cell = SVG_SIZE / grid;
    const out = [];
    for (let i = 0; i < grid * grid; i += 1) {
        const col = i % grid;
        const row = Math.floor(i / grid);
        const x = col * cell;
        const y = row * cell;
        out.push({
            path: `M ${x} ${y} L ${x + cell} ${y} L ${x + cell} ${y + cell} L ${x} ${y + cell} Z`,
        });
    }
    return out;
}

const FALLBACK_PATHS = buildSquarePiecePaths(MARKETING_GRID);

function resolveBaskets() {
    return normalizeBaskets(DEMO_BASKETS_6);
}

/** Basket 0 = top-left region — matches the dotted highlight on the preview. */
function pickConceptBasketIndex(baskets) {
    if (baskets.some((b) => b.index === 0)) return 0;
    return baskets[0]?.index ?? 0;
}

function normalizeBaskets(list) {
    if (!Array.isArray(list) || list.length === 0) return [];
    return list.slice(0, 8).map((b, i) => ({
        index: b.index != null ? Number(b.index) : i,
        dominantColorHex: b.dominantColorHex || "#64748b",
        pieceIds: Array.isArray(b.pieceIds) ? b.pieceIds : [],
    }));
}

/** White dotted box on the preview — top-left region this basket resolves. */
function ImageRegionHighlight() {
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                left: "5%",
                top: "5%",
                width: "42%",
                height: "42%",
                borderRadius: 1,
                border: "2.5px dashed rgba(255,255,255,0.95)",
                boxShadow: "0 0 0 1px rgba(15,23,42,0.35), 0 4px 12px rgba(0,0,0,0.35)",
                bgcolor: "rgba(56,189,248,0.12)",
                pointerEvents: "none",
                zIndex: 2,
            }}
        />
    );
}

/** Eight equal baskets stretched across full width — basket icon only (no corner badge). */
function BasketRow({ baskets, selectedIndex, onSelect, t, large = false }) {
    return (
        <Box
            role="listbox"
            aria-label={t(`${JM}.color-baskets-section`, "Color Baskets")}
            sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "flex-start",
                width: "100%",
                px: large ? 1.5 : 0.75,
                pt: large ? 1.75 : 1,
                pb: large ? 0.75 : 0.4,
                gap: large ? 1 : 0.55,
                flexShrink: 0,
                boxSizing: "border-box",
            }}
        >
            {baskets.map((basket) => {
                const displayN = basket.index + 1;
                const selected = basket.index === selectedIndex;
                const count = basket.pieceIds.length;
                return (
                    <Box
                        key={basket.index}
                        component="button"
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => onSelect(basket.index)}
                        aria-label={t(`${JM}.color-basket-row`, {
                            n: displayN,
                            count,
                            defaultValue: "Basket {{n}} · {{count}} pieces",
                        })}
                        sx={{
                            flex: "1 1 0",
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.4,
                            m: 0,
                            p: 0,
                            border: "none",
                            bgcolor: "transparent",
                            cursor: "pointer",
                            WebkitTapHighlightColor: "transparent",
                            "&:focus-visible": {
                                outline: "2px solid #38bdf8",
                                outlineOffset: 2,
                                borderRadius: 1.5,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                                maxWidth: large ? 84 : 56,
                                aspectRatio: "1 / 1",
                                borderRadius: 1.5,
                                bgcolor: basket.dominantColorHex,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "2px solid rgba(255,255,255,0.65)",
                                boxSizing: "border-box",
                                boxShadow:
                                    "0 4px 10px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.3)",
                                opacity: selected ? 1 : 0.92,
                                backgroundImage:
                                    "linear-gradient(145deg, rgba(255,255,255,0.28) 0%, transparent 45%, rgba(0,0,0,0.18) 100%)",
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 3,
                                    left: 3,
                                    ...colorBasketNumberBadgeSx({
                                        size: 16,
                                        highlighted: selected,
                                    }),
                                }}
                            >
                                {displayN}
                            </Box>
                            <FaShoppingBasket
                                size={22}
                                color="#fff"
                                aria-hidden
                                style={{ width: "48%", height: "48%" }}
                            />
                        </Box>
                        <Typography
                            sx={{
                                fontWeight: selected ? 800 : 700,
                                fontSize: large ? "1.05rem" : "0.7rem",
                                color: selected ? "#f8fafc" : "rgba(226,232,240,0.8)",
                                lineHeight: 1.15,
                                textAlign: "center",
                            }}
                        >
                            {t(`${JM}.color-basket-piece-count`, {
                                count,
                                defaultValue: "{{count}} pieces",
                            })}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

/**
 * Shot 02 — App Store Color Baskets mock (board in front of reference picture).
 * Keeps 5 pieces in home cells, and moves 5 more from the tray onto the board at random.
 */
export default function ColorBasketScreenshotLayout({
    headline,
    subtitle,
    baskets: _basketsProp,
    imageUrl,
    grid: storyGrid = 0,
    paths: storyPaths = null,
    matPreset,
    matColor,
    grooveStyle,
    highlightBasketIndex = null,
    landscape = false,
}) {
    const { t } = useScreenshotTranslation();
    const pathList = Array.isArray(storyPaths)
        ? storyPaths
        : Array.isArray(storyPaths?.pieces)
          ? storyPaths.pieces
          : null;
    const useLivePieces =
        Number(storyGrid) >= MARKETING_GRID &&
        Array.isArray(pathList) &&
        pathList.length >= MARKETING_GRID * MARKETING_GRID;
    const grid = MARKETING_GRID;
    const paths = useLivePieces ? pathList : FALLBACK_PATHS;
    const baskets = useMemo(() => resolveBaskets(), []);

    const [selectedIndex, setSelectedIndex] = useState(() => pickConceptBasketIndex(baskets));

    useEffect(() => {
        if (!baskets.length) return;
        setSelectedIndex(pickConceptBasketIndex(baskets));
    }, [baskets]);

    const selected =
        baskets.find((b) => b.index === selectedIndex) || baskets[0] || null;
    const pieceIds = selected?.pieceIds || [];

    // Keep 5 home-placed pieces; move 5 more from tray → board (random free placement).
    const homeBoardIndices = useMemo(() => {
        if (pieceIds.length === 0) return [];
        return pieceIds.slice(0, Math.min(5, pieceIds.length));
    }, [pieceIds]);

    const randomFromTrayIndices = useMemo(() => {
        if (pieceIds.length <= homeBoardIndices.length) return [];
        return pieceIds.slice(
            homeBoardIndices.length,
            homeBoardIndices.length + Math.min(5, pieceIds.length - homeBoardIndices.length),
        );
    }, [pieceIds, homeBoardIndices.length]);

    const boardPieceIndices = useMemo(
        () => [...homeBoardIndices, ...randomFromTrayIndices],
        [homeBoardIndices, randomFromTrayIndices],
    );

    const trayPieceIndices = useMemo(() => {
        const onBoard = new Set(boardPieceIndices);
        return pieceIds.filter((id) => !onBoard.has(id));
    }, [boardPieceIndices, pieceIds]);

    const cellPx = BOARD_SIZE / grid;
    const boardPiecePlacements = useMemo(() => {
        const homePlacements = homeBoardIndices.map((pieceIndex) => {
            const { x, y } = homePositionForPiece(pieceIndex, grid, BOARD_SIZE);
            return { pieceIndex, x, y, rotateDeg: 0, scattered: false };
        });
        const randomPlacements = buildScatteredBoardPlacements({
            pieceIndices: randomFromTrayIndices,
            boardSize: BOARD_SIZE,
            cellPx,
            seed: `color-basket-from-tray-5-${selected?.index ?? 0}`,
        });
        return [...homePlacements, ...randomPlacements];
    }, [homeBoardIndices, randomFromTrayIndices, grid, cellPx, selected?.index]);

    const nativePx = pieceVisualRenderSizePx(cellPx);
    const trayScale = 0.38;
    const trayCollapse = (nativePx * trayScale - nativePx) / 2;

    return (
        <Box
            sx={{
                ...colorBasketShellSx,
                background: "#0b1b3a",
                flexDirection: "column",
                alignItems: landscape ? "center" : "stretch",
                justifyContent: landscape ? "center" : undefined,
                px: landscape ? 4 : 0,
                backgroundImage:
                    "radial-gradient(ellipse at 50% 0%, rgba(56,189,248,0.12) 0%, transparent 55%)",
            }}
        >
            <Box
                sx={
                    landscape
                        ? {
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              width: "100%",
                              gap: 4,
                          }
                        : { display: "contents" }
                }
            >
            <Box
                sx={{
                    flexShrink: 0,
                    px: 2,
                    pt: landscape ? 0 : 0.25,
                    pb: landscape ? 0 : 0.65,
                    textAlign: landscape ? "left" : "center",
                    width: landscape ? "36%" : "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                }}
            >
                <Typography
                    component="h1"
                    sx={{
                        fontWeight: 800,
                        fontSize: landscape ? "3.55rem" : { xs: "1.35rem", sm: "1.55rem" },
                        letterSpacing: "-0.02em",
                        color: "#f8fafc",
                        lineHeight: 1.15,
                        textShadow: "0 2px 16px rgba(0,0,0,0.35)",
                    }}
                >
                    {headline}
                </Typography>
                {subtitle ? (
                    <Typography
                        component="p"
                        sx={{
                            mt: landscape ? 1.25 : 0.45,
                            fontWeight: landscape ? 600 : 500,
                            fontSize: landscape ? "1.9rem" : { xs: "0.74rem", sm: "0.8rem" },
                            lineHeight: 1.4,
                            color: "rgba(226,232,240,0.92)",
                            whiteSpace: "pre-line",
                            maxWidth: landscape ? "none" : 340,
                            mx: landscape ? 0 : "auto",
                        }}
                    >
                        {subtitle}
                    </Typography>
                ) : null}
            </Box>

            <Box
                sx={{
                    flex: landscape ? "0 1 auto" : 1,
                    height: landscape ? "auto" : undefined,
                    maxHeight: landscape ? "92%" : undefined,
                    minHeight: 0,
                    width: landscape ? "54%" : undefined,
                    maxWidth: landscape ? "56%" : undefined,
                    justifyContent: landscape ? "center" : undefined,
                    mx: landscape ? 0 : 1,
                    mb: landscape ? 0 : "max(env(safe-area-inset-bottom, 0px), 10px)",
                    borderRadius: 4,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    position: "relative",
                    background:
                        "linear-gradient(165deg, rgba(148,163,184,0.28) 0%, rgba(100,116,139,0.22) 40%, rgba(71,85,105,0.3) 100%)",
                    border: "1.5px solid rgba(226,232,240,0.45)",
                    boxShadow:
                        "0 24px 48px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.35)",
                    backdropFilter: "blur(18px)",
                }}
            >
                <BasketRow
                    baskets={baskets}
                    selectedIndex={selected?.index}
                    onSelect={setSelectedIndex}
                    t={t}
                    large={landscape}
                />

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: 0.75,
                        px: 1.25,
                        pb: 0.5,
                        flexShrink: 0,
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <Box
                        component="div"
                        sx={{
                            px: landscape ? 2.25 : 1.75,
                            py: landscape ? 0.9 : 0.55,
                            borderRadius: 999,
                            bgcolor: "rgba(148,163,184,0.55)",
                            border: "1px solid rgba(226,232,240,0.35)",
                            boxShadow: "0 6px 16px rgba(0,0,0,0.22)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 800,
                                fontSize: landscape ? "1.15rem" : "0.68rem",
                                letterSpacing: "0.08em",
                                color: "#f8fafc",
                                textTransform: "uppercase",
                            }}
                        >
                            {t(`${JM}.color-basket-deploy`, "Deploy to Board")}
                        </Typography>
                    </Box>
                </Box>

                {/* Concept: preview region (dotted) → basket board (same region to solve) */}
                <Box
                    sx={{
                        flex: landscape ? "0 0 auto" : 1,
                        height: landscape ? 400 : undefined,
                        minHeight: 0,
                        mx: 0.75,
                        mb: 0.75,
                        mt: 0.1,
                        borderRadius: 3,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        bgcolor: "rgba(15,23,42,0.72)",
                        border: "1px solid rgba(226,232,240,0.22)",
                        boxShadow: "0 12px 28px rgba(0,0,0,0.28)",
                        position: "relative",
                        zIndex: 2,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            position: "relative",
                            m: 0.5,
                            mb: trayPieceIndices.length ? 0 : 0.5,
                            borderRadius: 2,
                            overflow: "hidden",
                            bgcolor: "#1e293b",
                        }}
                    >
                        {/* Full image preview with region highlight */}
                        {imageUrl ? (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: { xs: 10, sm: 14 },
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    zIndex: 4,
                                    width: { xs: "58%", sm: "52%", md: "46%" },
                                    maxWidth: { xs: 220, sm: 280, md: 320 },
                                    aspectRatio: "1 / 1",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt=""
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: 2,
                                        border: "2.5px solid rgba(255,255,255,0.75)",
                                        boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
                                        display: "block",
                                    }}
                                />
                                <ImageRegionHighlight />
                            </Box>
                        ) : null}

                        {/* Basket board — full 6×6 */}
                        <Box
                            sx={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                bottom: 0,
                                top: { xs: "34%", sm: "36%" },
                                zIndex: 3,
                                overflow: "hidden",
                            }}
                        >
                            {imageUrl ? (
                                <ScreenshotBoard
                                    imageUrl={imageUrl}
                                    grid={grid}
                                    paths={paths}
                                    boardPieceIndices={boardPieceIndices}
                                    boardPiecePlacements={boardPiecePlacements}
                                    matPreset={matPreset}
                                    matColor={matColor || "#334155"}
                                    grooveStyle={grooveStyle}
                                    showDieLines
                                    fillHeight
                                    pieceBevel3d
                                />
                            ) : null}
                        </Box>
                    </Box>

                    {trayPieceIndices.length > 0 ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "nowrap",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 0.1,
                                px: 0.75,
                                py: 0.5,
                                minHeight: 44,
                                overflowX: "auto",
                                flexShrink: 0,
                                scrollbarWidth: "none",
                                "&::-webkit-scrollbar": { display: "none" },
                            }}
                        >
                            {trayPieceIndices.slice(0, 10).map((pieceIndex) => (
                                <Box
                                    key={`cb-tray-${pieceIndex}`}
                                    sx={{
                                        width: nativePx,
                                        height: nativePx,
                                        transform: `scale(${trayScale}) rotate(${
                                            ((pieceIndex * 17) % 24) - 12
                                        }deg)`,
                                        transformOrigin: "center center",
                                        m: `${trayCollapse}px`,
                                        flexShrink: 0,
                                        filter: "drop-shadow(0 3px 5px rgba(0,0,0,0.35))",
                                    }}
                                >
                                    <PieceVisual
                                        pieceIndex={pieceIndex}
                                        imageUrl={imageUrl}
                                        grid={grid}
                                        paths={paths}
                                        showGridCoords={false}
                                        anchorToCell={false}
                                        bevel3d
                                    />
                                </Box>
                            ))}
                        </Box>
                    ) : null}
                </Box>
            </Box>
            </Box>
        </Box>
    );
}
